import { SiteItemData } from "../../../../interfaces";

/** Feed media item with optional preroll flags (same as DesignItemProps.videoItem). */
export type RylzFeedMediaItem = SiteItemData & {
    hidePrerollAds?: boolean
    prerollIndex?: number
};

export type RylzFeedSlide = RylzFeedMediaItem | string;

export interface RylzFeedAdsOptions {
    overlayItems: SiteItemData[]
    showPrerollAds: boolean
    firstAdPlace: number
    maxAds: number
    repeatAds: number
    /** Mobile native slots only — mirrors Vertical Mutam. Desktop ignores this. */
    allowNativeAds?: boolean
}

/**
 * - showPrerollAds → preroll on selected videos (no native slides)
 * - otherwise → insert ads.vertical.native.* slides between items
 */
export function getRylzListWithAds(options: RylzFeedAdsOptions): RylzFeedSlide[] {
    const {
        overlayItems,
        showPrerollAds,
        firstAdPlace,
        maxAds,
        repeatAds,
        allowNativeAds = true,
    } = options;

    if (showPrerollAds) {
        return getListWithPreRollsAds(overlayItems, firstAdPlace, maxAds, repeatAds, showPrerollAds);
    }

    if (maxAds === 0 || !allowNativeAds) {
        return [...overlayItems];
    }

    const videoListCopy = [...overlayItems];
    const videoListLength = videoListCopy.length;
    let numberOfAds = firstAdPlace > videoListLength
        ? 0
        : Math.min(maxAds, videoListLength - firstAdPlace, Math.floor((videoListLength - firstAdPlace) / repeatAds + 1));
    const newListLength = videoListLength + numberOfAds;
    const baseId = "ads.vertical.native.";
    const newList: RylzFeedSlide[] = [];
    let adCount = 1;

    for (let i = 0; i < newListLength; i++) {
        if (i < firstAdPlace) {
            newList.push(videoListCopy.shift());
        } else if (i === firstAdPlace && numberOfAds > 0) {
            newList.push(`${baseId}${adCount}`);
            numberOfAds--;
            adCount++;
        } else if (numberOfAds > 0 && (i - firstAdPlace) % (repeatAds + 1) === 0) {
            newList.push(`${baseId}${adCount}`);
            numberOfAds--;
            adCount++;
        } else {
            newList.push(videoListCopy.shift());
        }
    }

    return newList;
}

function getListWithPreRollsAds(
    overlayItems: SiteItemData[],
    firstAdPlace: number,
    maxAds: number,
    repeatAds: number,
    showPrerollAds: boolean,
): RylzFeedSlide[] {
    const videoListLength = overlayItems.length;
    let numberOfAdsLeft = firstAdPlace > videoListLength
        ? 0
        : Math.min(maxAds, videoListLength - firstAdPlace, Math.floor((videoListLength - firstAdPlace) / repeatAds + 1));
    const totalAdsNumber = firstAdPlace > videoListLength
        ? 0
        : Math.min(maxAds, videoListLength - firstAdPlace, Math.floor((videoListLength - firstAdPlace) / repeatAds + 1));

    if (maxAds === 0) return [...overlayItems];

    const newList: RylzFeedSlide[] = [];

    for (let i = 0; i < overlayItems.length; i++) {
        const prerollIndex = Math.abs(numberOfAdsLeft - (totalAdsNumber + 1));
        if (i + 1 < firstAdPlace) {
            newList.push({ ...overlayItems[i], hidePrerollAds: true, prerollIndex: undefined } as RylzFeedMediaItem);
        } else if (i === firstAdPlace && numberOfAdsLeft > 0) {
            newList.push({
                ...overlayItems[i],
                hidePrerollAds: false,
                prerollIndex: showPrerollAds ? prerollIndex : undefined,
            } as RylzFeedMediaItem);
            numberOfAdsLeft--;
        } else if (numberOfAdsLeft > 0 && (i - firstAdPlace) % (repeatAds + 1) === 0) {
            newList.push({
                ...overlayItems[i],
                hidePrerollAds: false,
                prerollIndex: showPrerollAds ? prerollIndex : undefined,
            } as RylzFeedMediaItem);
            numberOfAdsLeft--;
        } else {
            newList.push({ ...overlayItems[i], hidePrerollAds: true, prerollIndex: undefined } as RylzFeedMediaItem);
        }
    }

    return newList;
}

/** Force a preroll on the slide the user opened into (Vertical desktop parity). */
export function applyPrerollOnVideoEntrance(
    list: RylzFeedSlide[],
    selectedVideo: number,
    prerollOnVideoEntrance: boolean,
): RylzFeedSlide[] {
    if (!prerollOnVideoEntrance || selectedVideo < 0 || selectedVideo >= list.length) return list;
    const item = list[selectedVideo];
    if (typeof item === "string") return list;
    const newList = [...list];
    newList[selectedVideo] = {
        ...item,
        hidePrerollAds: false,
        prerollIndex: item.prerollIndex || 1,
    };
    return newList;
}

export function findFeedIndexForItem(list: RylzFeedSlide[], itemId: string): number {
    return list.findIndex((element) => {
        if (typeof element === "string") return false;
        return element.itemId === itemId;
    });
}
